import { KeyboardNavigable, NonRollingControlMode, KeyboardNavigableEventTypes } from './keyboard-navigation';

/**
 * Two PredingtingInputField element can be linked together by giving them
 * data-attribute data-min or data-max. Value is a selector point to the linked element.
 */
class MinMaxLinkMode {

  /**
   * @property {PredictingInputField} partner - Linked element `A`
   */
  #element: PredictingInputField;

  /**
   * @property {PredictingInputField} partner - Linked element `B`
   */
  #partner: PredictingInputField;

  /**
   * @constructor
   * @param {PredictingInputField} element element A
   * @param {PredictingInputField} partner element B
   */
  constructor( element:PredictingInputField, partner:PredictingInputField ) {
    this.#element = element;
    this.#partner = partner;
  }

  /**
   * Function is called when value changes in input field.
   * @param {string} fieldtype value is 'min' value is changed in a input with a minimun value and 'max' if value is changed in maximum field.
   */
  action( fieldtype:string ) {
    if( fieldtype === 'min' ) {
      if( parseFloat( this.#element.value ) >= parseFloat( this.#partner.value ) ) {
        this.#partner.value = '';
      }
    }
    if( fieldtype === 'max' ) {
      if( parseFloat( this.#element.value ) <= parseFloat( this.#partner.value ) ) {
        this.#partner.value = '';
      }
    }
  }
}


/**
 * Allowed search strategies are allowed here
 */
enum SEARCH_STRATEGY {
  SMALLER_OR_EQUAL = 'SMALLER_OR_EQUAL',
  ORDER_BY_MAGNITUDE = 'ORDER_BY_MAGNITUDE',
  DEFAULT = 'DEFAULT'
}

/**
 * Search mode
 */
const searchMode = {

  /**
   * Default simple search
   * @param {string} searchValue user's search input
   * @param {HTMLElement[]} options option HTMLElement object in an array
   * @returns {object[]} matching options
   */
  [SEARCH_STRATEGY.DEFAULT](searchValue:string, options:HTMLElement[]) {
    return options.filter( option => {
      return String( option.dataset.value ).startsWith( searchValue );
    });
  },

  /**
   * Search result by combination of order of magnitude and rounding.
   * User inputs 4600 it's rounded to 5000, or if user inputs 34 it's rounded 30 and so and so on.
   * Rounding works big or small numbers.
   *
   * @param {string} searchValue user's search input
   * @param {HTMLElement[]} options option HTMLElement object in an array
   * @returns {object[]} matching options
   */
  [SEARCH_STRATEGY.ORDER_BY_MAGNITUDE](searchValue:string, options:HTMLElement[]) {

    const numericSearchValue = parseFloat( searchValue );
    const magnitude = Math.floor(Math.log(numericSearchValue) / Math.LN10 + 0.000000001);
    const value = Math.pow(10,magnitude);
    const magnitudeRoundedValue = Math.round(numericSearchValue/value)*value;

    return options.filter( option => {
      return String( option.dataset.value ).startsWith( String( magnitudeRoundedValue ) );
    });
  },

  /**
   * Search result contains value that are numerically smaller or equal values as search value.
   * @param {string} searchValue user's search input
   * @param {HTMLElement[]} options option HTMLElement object in an array
   * @returns {object[]} matching options
   */
  [SEARCH_STRATEGY.SMALLER_OR_EQUAL]( searchValue:string, options:HTMLElement[] ) {
    const numericSearchValue = parseFloat( searchValue );

    return options.filter( option => {
      return parseFloat( option.dataset.value ) <= numericSearchValue;
    });
  }
}



/**
 * PredictingInputFieldDomElements contains all HTMlElements that single PredictingInputField
 */
interface PredictingInputFieldDomElements {
  input?: HTMLInputElement;
  inputFieldHolder?: HTMLElement;
  optionsHolder?: HTMLElement;
  valueDisplay?: HTMLElement;
  options?: NodeListOf<HTMLElement>;
  errorMessage?: HTMLElement;
}

/**
 * These are supplied trough HTML data attributes.
 */
interface PredictingInputFieldOptions {
  searchStrategy?:SEARCH_STRATEGY
  linkmodeType?:string
  linkmode?:MinMaxLinkMode,
  fieldtype?:string,
}


/**
 * PredictingInputField
 *
 * This class works with HTML printed with cm-scriptlet://mustache-predicting-input-field/.
 * More about it here https://veho-admin.stage.crasman.cloud/fi/system/veho-fi-atoms/
 */
class PredictingInputField {

  /**
   * All business logic is binded to this and it child elements.
   */
  #element:HTMLElement;

  /**
   * Implements keyboard navigation
   */
  #keyboardNavigable:KeyboardNavigable;

  /**
   * HTMLElement references are stored here for a later use
   */
  #dom:PredictingInputFieldDomElements = {};

  /**
   * If field is used as a min max field, fields need to be linked together somehow.
   */
  #partner?:PredictingInputField;

  /**
   * PredictingInputFieldOptions are stored here
   */
  options:PredictingInputFieldOptions;

  /**
   * Link mode handles how min & max selection are handled.
   */
  #linkmode?:MinMaxLinkMode;

  /**
   * @constructor
   * @param {HTMLElement} containerElement container element
   * @param {HTMLElement} element classes business logic is bind to this element and it child elements.
   * @param {object} [options] control options
   */
  constructor( containerElement:HTMLElement, element:HTMLElement, options:PredictingInputFieldOptions={} ) {

    this.#element = element;

    this.#keyboardNavigable = new KeyboardNavigable(
      new NonRollingControlMode( this.getFocusableElements() ),
      containerElement
    );

    this.findElements();
    this.bindEventListeners();

    this.#keyboardNavigable.bindKeyboardNavitable(this.#element);

    this.#keyboardNavigable.subscribe( KeyboardNavigableEventTypes.ON_ENTER_PRESSED, this.onEnterPressed.bind(this) );
    this.#keyboardNavigable.subscribe( KeyboardNavigableEventTypes.ON_SPACE_PRESSED, this.onEnterPressed.bind(this) );
    this.#keyboardNavigable.subscribe( KeyboardNavigableEventTypes.ON_TAB_PRESSED, this.tabOut.bind(this) );

    this.options = options;

    if( options.linkmode ) {

      if( !['min', 'max'].includes( this.options.fieldtype ) ) {
        throw new TypeError(`Invalid fieldtype supplied, supplied value was ${this.options.fieldtype}. Allowed modes are 'min' or 'max'`);
      }

      this.#linkmode = this.options.linkmode;
    }
  }

  /**
   * Return field id
   * @return {string} return field id
   */
  get id():string {
    return this.#dom.input.id;
  }

  /**
   * Set partner input field. _partner field is used when a two fields interact together
   * like in a case of min & max fields.
   * @param {PredictingInputField} predictingInputField partner field
   * @throw {TypeError} when supplied value is not a instance of PredictingInputField class
   */
  set partner( predictingInputField:PredictingInputField ) {
    this.#partner = predictingInputField;
  }

  /**
   * If two instances of PredictingInputField works together as a pair this
   * returns field's partner instance.
   * @return {PredictingInputField} partner
   */
  get partner() {
    return this.#partner;
  }

  /**
   * Set value to input element.
   * If given value is an empty string value is emptied and linkMode function is executed.
   * @param {string} value - value to be set
   * @throws {Error} if value is not one existing options
   */
  set value( value ) {

    if( !(typeof value === 'string' && value.length === 0) &&
       !Array.from(this.#dom.options).find( option => option.dataset.value === value )) {
      throw new Error(`Given value don't exist in options, can not set the value! Given value was ${value}`)
    }

    this.#dom.input.value = value;

    if( value !== '' ) {
      this.#linkmode?.action(this.options.fieldtype);
    }
  }

  /**
   * Return internal input element value
   * @returns {string} input element value
   */
  get value() {
    return this.#dom.input.value;
  }

  /**
   * Populate #dom object with a HTMLElement references
   */
  private findElements() {

    this.#dom.input = this.#element.querySelector('input');
    this.#dom.inputFieldHolder = this.#element.querySelector('.predicting-input-field-holder');
    this.#dom.optionsHolder = this.#element.querySelector('.predicting-input-field-options');
    this.#dom.valueDisplay = this.#element.querySelector('.redicting-input-field-value-display');
    this.#dom.options = this.#element.querySelectorAll('.predicting-input-field-options span.option');
    this.#dom.errorMessage = this.#element.querySelector('.predicting-input-field-validation-message');

    this.updateFocusableElements();
  }

  /**
   * Bind event listeners
   */
  private bindEventListeners() {

    this.#dom.input.addEventListener('input', (event:InputEvent) => {
      const input:HTMLInputElement = event.target as HTMLInputElement;
      this.doSearch(input.value);
    });

    this.#dom.input.addEventListener('focus', this.onFocused.bind(this));

    //Clicked outside of the element
    window.addEventListener('click', ( event:PointerEvent ) => {

      const target:HTMLElement = event.target as HTMLElement;

      if (!target.closest('.predicting-input-field')) {
        this.closeComponent();
        if( !this.validateSearchValue(this.value) || !this.isOneOfOptionsValues(this.value)) {
          this.emptyValue();
        }
      }
    });


    this.#dom.optionsHolder.addEventListener( 'click', this.onOptionClicked.bind(this) );
  }

  /**
   * Tabbing out
   */
  private tabOut() {
    if( !this.validateSearchValue(this.value) || !this.isOneOfOptionsValues(this.value)) {
      this.emptyValue();
    }

    //Small delay gives time browser to move focus outside the container element.
    setTimeout( () => {
      if( !(document.activeElement === this.#dom.input ||( document.activeElement === (this.#partner && this.#partner.#dom.input) ) ) ) {
        this.closeComponent();
      }
    }, 100)
  }

  /**
   * Empty value and hide validation errors
   */
  private emptyValue() {
    this.value = '';
    this.showValidationErrors(true);
  }

  /**
   * Check is a value one of the options
   * @param {string} value typically user inputted value
   * @return {boolean} true if value is one of options otherwise false
   */
  private isOneOfOptionsValues(value:string) {
    const options =  Array.from(this.#dom.options);
    return options.some( option => option.dataset.value === value );
  }


  private getFocusableElements():HTMLElement[] {
    return Array.from( this.#element.querySelectorAll('input, span.option.show') );
  }

  /**
   * Update all keyboard focusabe elements. This is purely used for make component keyboardnavigable.
   */
  private updateFocusableElements() {
    this.#keyboardNavigable.setFocusableElements(this.getFocusableElements())
  }

  /**
   * Show or hide field options
   * @param {boolean} show true if options are shown false if they are hidden
   */
  private showOptions(show:boolean) {
    this.#element.classList.toggle('predicting-input-field--open', show);
    this.#dom.optionsHolder.classList.toggle('show', show );
  }

  /**
   * Close field options
   */
  private closeComponent() {
    this.showOptions(false);
  }

  // --- Event handlers ---

  /**
   * Called when the field is focused
   */
  private onFocused() {
    if( this.validateSearchValue(this.value) ) {

      this.#dom.options.forEach( option => {
        option.classList.toggle('show', true);
      });

      this.showOptions(true);
    }

    PredictingInputField.closeComponents(this);
  }

  /**
   * On option is clicked
   * @param {Event} event event object
   */
  private onOptionClicked(event:PointerEvent) {

    const target = event.target as HTMLElement;

    if( target.nodeName === 'SPAN' && target.classList.contains('option') ) {
      this.value = target.dataset.value;
      this.#keyboardNavigable.setFocusedElement( target );
      this.closeComponent();
    }
  }

  /**
   * On enter pressed
   * @param {any} payload currently focused element
   */
  private onEnterPressed( payload:any ) {

    const focusedElement = payload.focusedElement as HTMLElement;

    if( !this.validateSearchValue(this.value) ) {
      return;
    }

    if( focusedElement?.tagName === 'SPAN' && focusedElement?.dataset.value ) {
      this.value = focusedElement.dataset.value;
    } else {
      const options = Array.from( this.#dom.options ).filter( option => option.classList.contains('show') ).reverse();
      if( options.length > 0 ) {
        this.value = options[0].dataset.value;
      }
    }

    this.closeComponent();

    this.#keyboardNavigable.setFocusedElement( this.getFocusableElements()[0] );//Return focus to input element
  }

  /**
   * Validate that search value is numeric value.
   * @public
   * @param {string} searchValue numeric value
   * @return {boolean} return true is value is valid false if not
   */
  validateSearchValue( searchValue:string ) {
    let value;

    if( typeof searchValue === 'string') {
      value = parseFloat( searchValue.replace(',', '.') )
    } else if( typeof searchValue === 'number' ) {
      value = searchValue;
    }

    return searchValue === '' || typeof value === 'number' && !isNaN( value );
  }

  /**
   * Called when a user type someting to the field
   * @param {string} searchValue input event
   */
  private doSearch( searchValue:string ) {

    let isValid = this.validateSearchValue(searchValue);

    if( isValid ) {

      const options =  Array.from(this.#dom.options);
      const results = this.searchOptions( searchValue, options);

      //Show or hide option elements
      options.forEach( option => {
        option.classList.toggle('show', Boolean(results.find( foundOption => foundOption === option )));
      });

      this.showOptions(results.length > 0);
      this.#dom.input.classList.toggle('black', this.#dom.input.value.length > 0);
      this.updateFocusableElements();

      const lastVisibleOption = options.reverse().find( option => option.classList.contains('show') );

      if( lastVisibleOption ) {
        lastVisibleOption.scrollIntoView({block: 'end', inline: 'nearest', behavior: 'auto'});
      }

      isValid = results.length > 0;
    }

    this.showValidationErrors(isValid);
  }

  /**
   * Search options
   * @param {string} searchValue search value from input field
   * @param {object} options options
   * @returns {object[]} search results
   */
  private searchOptions( searchValue:string, options:HTMLElement[] ):HTMLElement[] {
    if( searchValue === '' ) {
      return options;
    } else {
      return searchMode[ this.options.searchStrategy ]( searchValue, options )
    }
  }

  /**
   * Show or hide validation errors
   * @param {boolean} isValid true if search value is valid false otherwise
   */
  private showValidationErrors(isValid:boolean) {
    this.#element.classList.toggle('error', !isValid );
    if(!isValid) {
      this.showOptions(false);
    }
  }

  /**
   * @static
   * @property {PredictingInputField[]} instances - all existing instances of PredictingInputField in DOM are stored here.
   * This is used to link min / max functionalities
   */
  static instances:PredictingInputField[] = [];

  /**
   * Close all predicting input fields
   * @param {PredictingInputField} [closingInstance] this instance _closeComponent() function is not called
   */
  static closeComponents( closingInstance:PredictingInputField ) {
    PredictingInputField.instances.filter( instance => instance !== closingInstance )
      .forEach( instance => {
        instance.closeComponent();
        if( !instance.validateSearchValue(instance.value) || !instance.isOneOfOptionsValues(instance.value)) {
          instance.emptyValue();
        }
      });
  }

  /**
   * Called to initialize predicting input fields in the document object model
   */
  static init() {

    document.querySelectorAll('.predicting-input-field-container').forEach( (containerElement:HTMLElement) => {

      const pairs:PredictingInputField[] = [];

      containerElement.querySelectorAll('.predicting-input-field').forEach( (element:HTMLElement) => {

        let searchStrategy:SEARCH_STRATEGY = SEARCH_STRATEGY.DEFAULT;

        const containerSearchStrategy = containerElement.dataset.searchStrategy?.toLocaleUpperCase() as SEARCH_STRATEGY

        if( Object.keys( SEARCH_STRATEGY ).includes( containerSearchStrategy ) ) {
          searchStrategy = containerSearchStrategy;
        } else {
          console.info(`Non allowed search strategy supplied, given value was ${containerSearchStrategy}. Allowed values are ${Object.keys( SEARCH_STRATEGY )}. Using the default one for now.`);
        }

        const options:PredictingInputFieldOptions = {
          linkmodeType: (element.dataset.min || element.dataset.max ) ? 'minmax' : '',
          fieldtype: element.dataset.min ? 'max' : element.dataset.max ? 'min' : undefined,
          searchStrategy: searchStrategy
        }

        const instance = new PredictingInputField(containerElement, element, options);
        pairs.push( instance )
        PredictingInputField.instances.push(instance);
      });

      if( pairs.length === 2 ) {
        pairs[0].partner = pairs[1];
        pairs[1].partner = pairs[0];
        pairs[0].#linkmode = new MinMaxLinkMode( pairs[0], pairs[1] );
        pairs[1].#linkmode = new MinMaxLinkMode( pairs[1], pairs[0] );
      }
    });
  }
}



export {
  PredictingInputField,
  PredictingInputFieldOptions
}
